
import { useEffect, useState } from "react";
import ApiServices from "../services/apiservices";
import { Link } from "react-router-dom";
import { FiClock } from "react-icons/fi";

const Publications = () => {
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");

  const fetchPosts = async () => {
    try {
      const res = await ApiServices.getRequest(`/posts/published.php`);
      const fetchedPosts = res.posts || [];

      setPosts(fetchedPosts);
      setFilteredPosts(fetchedPosts);

      const uniqueCategories = [
        "All",
        ...new Set(fetchedPosts.map((p) => p.category)),
      ];

      setCategories(uniqueCategories);
    } catch (error) {
      console.error("Error fetching publications", error);
    }
  };

  useEffect(() => {
    fetchPosts();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleFilter = (category) => {
    setActiveCategory(category);

    if (category === "All") {
      setFilteredPosts(posts);
    } else {
      const filtered = posts.filter((p) => p.category === category);
      setFilteredPosts(filtered);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString("en-NG", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="w-full min-h-screen bg-white font-sans">
   <div className="relative bg-[#14532d] text-white py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}> 
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between">
            <div className="lg:w-2/3">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight  uppercase tracking-tight">
              All<span className="text-yellow-400">Publications</span>
              </h1>
              <div className="w-20 h-1.5 bg-yellow-400 rounded-full mb-6"></div>
            </div>
          
          </div>
        </div>
    </div>

      <div className="max-w-5xl mx-auto px-4 py-8">

        {/* CATEGORY FILTER */}
        <div className="flex flex-wrap gap-2.5 mb-8">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => handleFilter(cat)}
              className={`px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all border
                ${
                  activeCategory === cat
                    ? "bg-green-700 text-white border-green-700"
                    : "bg-white text-gray-500 border-gray-200 hover:border-green-700 hover:text-green-700"
                }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* EMPTY STATE */}
        {filteredPosts.length === 0 && (
          <div className="text-center text-gray-400 py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
            <div className="text-4xl mb-4">📂</div>
            <p className="text-base font-bold uppercase tracking-tight">
              No publications found.
            </p>
          </div>
        )}

        {/* POSTS LIST */}
        <div className="grid grid-cols-1 gap-4">
          {filteredPosts.map((post) => (
            <Link
              key={post.id}
              to={`/${post.postLinkTitle}`}
              className="group block bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-1"
            >
              <div className="flex flex-col md:flex-row">
                {/* IMAGE */}
                <div className="md:w-[220px] w-full h-[140px] md:h-auto overflow-hidden relative flex-shrink-0">
                  {post.featuredImage ? (
                    <img
                      src={post.featuredImage}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center text-gray-400 text-[10px] font-bold uppercase">
                      No Image
                    </div>
                  )}

                  <div className="absolute top-2.5 left-2.5 bg-yellow-400 text-black px-2.5 py-1 rounded-full text-[9px] font-extrabold uppercase tracking-widest shadow">
                    {post.category}
                  </div>
                </div>

                {/* CONTENT */}
                <div className="flex-1 p-4 md:p-5 flex flex-col justify-between">
                  <div>
                    <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2 uppercase tracking-tight group-hover:text-green-700 transition-colors line-clamp-1">
                      {post.title}
                    </h3>

                    <p className="text-gray-600 text-xs md:text-sm leading-relaxed line-clamp-2">
                      {post.introContent || "Click to read full publication details."}
                    </p>
                  </div>

                  {/* META */}
                  <div className="flex items-center mt-4 pt-3 border-t border-gray-100 text-[10px]">
                    <div className="flex items-center gap-2 text-gray-400 font-bold uppercase tracking-wider">
                      <FiClock className="text-green-700 text-sm" />
                      {formatDate(post.createdAt)}
                    </div>

                    <span className="ml-auto text-green-700 font-bold uppercase tracking-wider group-hover:translate-x-1 transition-transform">
                      View →
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Publications;